import { useTheme } from '@mui/material';
import DSModal from '../../../design-system/DSModal';
import DSBox from '../../../design-system/DSBox';
import DSSlider from '../../../design-system/DSSlider';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import {
  DSTitleTypography,
  DSTypography,
} from '../../../design-system/Typography';
import DSValueTypography from '../../../design-system/Typography/DSValueTypography';
import KlydoThumbnailFilmstrip from './KlydoThumbnailFilmstrip';
import useKlydoThumbnailPicker from '../hooks/useKlydoThumbnailPicker';
import {
  formatFrameOffset,
  getKlydoStillFrameUrl,
  THUMBNAIL_SCRUB_STEP_SECONDS,
} from '../../../utils/klydoThumbnail';

const COPY = {
  title: 'Choose your thumbnail frame',
  subtitle:
    'This frame stands in for your Klydo wherever it is not animating — your artist page, collections and search.',
  suggestions: 'Suggested frames',
  cancel: 'Cancel',
  confirm: 'Use this frame',
};

type KlydoThumbnailPickerModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (offsetSeconds: number) => void;
  loopUrl: string;
  offsetSeconds: number | null;
  durationInSeconds?: number;
};

const KlydoThumbnailPickerModal = ({
  open,
  onClose,
  onConfirm,
  loopUrl,
  offsetSeconds,
  durationInSeconds,
}: KlydoThumbnailPickerModalProps) => {
  const { spacing, palette } = useTheme();

  const {
    videoRef,
    videoUrl,
    duration,
    previewOffsetSeconds,
    filmstripFrames,
    selectedFilmstripOffsetSeconds,
    handleOffsetChange,
    handleLoadedMetadata,
    handleSeeked,
    handleVideoError,
    handleConfirm,
  } = useKlydoThumbnailPicker({
    open,
    loopUrl,
    offsetSeconds,
    durationInSeconds,
    onConfirm,
  });

  return (
    <DSModal open={open} onClose={onClose}>
      <DSBox
        sx={{
          width: spacing(70),
          backgroundColor: palette.background.paper,
          padding: spacing(3),
          borderRadius: spacing(3),
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(2),
        }}
      >
        <DSTitleTypography>{COPY.title}</DSTitleTypography>
        <DSValueTypography sx={{ color: palette.text.secondary }}>
          {COPY.subtitle}
        </DSValueTypography>

        <DSBox
          sx={{
            alignSelf: 'center',
            width: spacing(28),
            height: spacing(28),
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: palette.white[5],
          }}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            poster={getKlydoStillFrameUrl({ loopUrl, offsetSeconds })}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={handleLoadedMetadata}
            onSeeked={handleSeeked}
            onError={handleVideoError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing(2),
          }}
        >
          <DSSlider
            aria-label="Thumbnail frame"
            value={previewOffsetSeconds}
            min={0}
            max={duration || THUMBNAIL_SCRUB_STEP_SECONDS}
            step={THUMBNAIL_SCRUB_STEP_SECONDS}
            marks={filmstripFrames.map((frame) => ({
              value: frame.offsetSeconds,
            }))}
            disabled={duration <= 0}
            valueLabelDisplay="off"
            onChange={(_, value) => handleOffsetChange(value as number)}
          />
          <DSValueTypography
            sx={{
              minWidth: spacing(7),
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatFrameOffset(previewOffsetSeconds)}
          </DSValueTypography>
        </DSBox>

        <DSBox
          sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}
        >
          <DSTypography variant="label">{COPY.suggestions}</DSTypography>
          <KlydoThumbnailFilmstrip
            frames={filmstripFrames}
            selectedOffsetSeconds={selectedFilmstripOffsetSeconds}
            onSelect={handleOffsetChange}
          />
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spacing(2),
            marginTop: spacing(1),
          }}
        >
          <DSSecondaryButton onClick={onClose}>{COPY.cancel}</DSSecondaryButton>
          <DSPrimaryButton onClick={handleConfirm} disabled={duration <= 0}>
            {COPY.confirm}
          </DSPrimaryButton>
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

export default KlydoThumbnailPickerModal;
